import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Input,
  IconButton,
  Text,
  Button,
  Checkbox,
} from '@chakra-ui/react';
import { MdAdd, MdDelete } from 'react-icons/md';
import {
  ApprovalTemplateBuilderUI,
  ApprovalTemplateHeaderUI,
  ApprovalVariableSourceUI,
  ApprovalVariableKind,
} from '@/lib/sdk/chatbot/types';

const BODY_KIND_OPTIONS: { value: ApprovalVariableKind; label: string }[] = [
  { value: 'static', label: 'Static text' },
  { value: 'maker_name', label: "Maker's name" },
  { value: 'task_name', label: 'Task name' },
  { value: 'current_value', label: 'Submitted value (this task)' },
  { value: 'task_value', label: 'Value from another task' },
  { value: 'task_attr', label: 'Attribute from another task (e.g. choice code/label)' },
];
const TEXT_KINDS = BODY_KIND_OPTIONS.map((opt) => opt.value);

// Button payloads additionally allow approval_decision — payload-only, valid only here, never
// in a body variable or a header. Lets a maker-checker level's own button replace maker.go's
// built-in approve/reject buttons.
const BUTTON_PAYLOAD_KIND_OPTIONS: { value: ApprovalVariableKind; label: string }[] = [
  ...BODY_KIND_OPTIONS,
  { value: 'approval_decision', label: 'Approval decision (approve/reject this level)' },
];

const MEDIA_HEADER_SOURCE_KIND_OPTIONS: { value: ApprovalVariableKind; label: string }[] = [
  { value: 'image_link', label: 'Stored image link' },
  { value: 'current_upload', label: "Maker's just-uploaded file" },
  { value: 'generated_image', label: 'Generate an image from a value' },
];

interface AvailableTask {
  id: string;
  name: string;
}

function emptyVariable(kind: ApprovalVariableKind): ApprovalVariableSourceUI {
  return { kind };
}

interface VariableRowProps {
  variable: ApprovalVariableSourceUI;
  kindOptions: { value: ApprovalVariableKind; label: string }[];
  availableTasks: AvailableTask[];
  onChange: (variable: ApprovalVariableSourceUI) => void;
  onRemove?: () => void;
}

// One variable: a kind picker plus whatever kind-specific inputs that kind needs. task_value,
// task_attr, and image_link all read platform.CommandArgs.TaskRep by default (an unset taskRef
// means "this task" — see resolveTaskRep in internal/workflow/messages/builder_resolver.go), so
// the task picker is a placeholder-only "This task" rather than a required field.
const VariableRow = ({ variable, kindOptions, availableTasks, onChange, onRemove }: VariableRowProps) => (
  <Box borderWidth="1px" borderColor="border-default" borderRadius="md" p={2} bg="bg-surface">
    <HStack spacing={2} align="center">
      <Select
        size="sm"
        bg="bg-surface"
        value={variable.kind}
        onChange={(e) => onChange(emptyVariable(e.target.value as ApprovalVariableKind))}
      >
        {kindOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </Select>
      {onRemove && (
        <IconButton
          icon={<MdDelete />}
          aria-label="Remove variable"
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={onRemove}
        />
      )}
    </HStack>

    {variable.kind === 'static' && (
      <Input
        mt={2}
        size="sm"
        bg="bg-surface"
        placeholder="Literal text"
        value={variable.static || ''}
        onChange={(e) => onChange({ ...variable, static: e.target.value })}
      />
    )}

    {(variable.kind === 'task_value' || variable.kind === 'task_attr' || variable.kind === 'image_link') && (
      <Select
        mt={2}
        size="sm"
        bg="bg-surface"
        value={variable.taskRef || ''}
        onChange={(e) => onChange({ ...variable, taskRef: e.target.value || undefined })}
        placeholder="This task"
      >
        {availableTasks.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </Select>
    )}

    {variable.kind === 'task_attr' && (
      <Input
        mt={2}
        size="sm"
        bg="bg-surface"
        placeholder="Attribute field name (e.g. code, label)"
        value={variable.field || ''}
        onChange={(e) => onChange({ ...variable, field: e.target.value })}
      />
    )}

    {variable.kind === 'approval_decision' && (
      <Select
        mt={2}
        size="sm"
        bg="bg-surface"
        value={variable.decision || ''}
        onChange={(e) => onChange({ ...variable, decision: e.target.value as 'approved' | 'rejected' })}
        placeholder="Decision"
      >
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
      </Select>
    )}
  </Box>
);

interface ApprovalTemplateBuilderSectionProps {
  builder: ApprovalTemplateBuilderUI;
  availableTasks: AvailableTask[];
  onChange: (builder: ApprovalTemplateBuilderUI) => void;
  // Quick-reply buttons: for a one-way alert (team_alert.go's flat template_builder), any
  // buttons. For a maker-checker level: leaving buttons empty keeps maker.go's built-in
  // approve/(reject) buttons; defining buttons here (using the "Approval decision" payload
  // kind) fully replaces that built-in behaviour instead (see maker.go's sendApprovalTemplate).
  allowButtons?: boolean;
}

// Configures a level's declarative template — an optional header, an ordered list of body
// variables, and (for one-way alerts) quick-reply buttons — for the 'builder' approval_resolver
// (docs/maker-checker-design.md "Template builder resolver"). Every {{n}} placeholder the
// WhatsApp template declares needs exactly one body variable here, in the same order.
export const ApprovalTemplateBuilderSection = ({ builder, availableTasks, onChange, allowButtons }: ApprovalTemplateBuilderSectionProps) => {
  const body = builder.body ?? [];
  const buttons = builder.buttons ?? [];

  const handleAddBodyVariable = () => {
    onChange({ ...builder, body: [...body, emptyVariable('static')] });
  };

  const handleBodyVariableChange = (index: number, variable: ApprovalVariableSourceUI) => {
    onChange({ ...builder, body: body.map((v, i) => (i === index ? variable : v)) });
  };

  const handleRemoveBodyVariable = (index: number) => {
    onChange({ ...builder, body: body.filter((_, i) => i !== index) });
  };

  const handleHeaderToggle = (enabled: boolean) => {
    if (!enabled) {
      onChange({ ...builder, header: undefined });
      return;
    }
    onChange({
      ...builder,
      header: { type: 'image', source: emptyVariable('image_link') },
    });
  };

  const handleHeaderChange = (header: ApprovalTemplateHeaderUI) => {
    onChange({ ...builder, header });
  };

  const handleAddButton = () => {
    onChange({ ...builder, buttons: [...buttons, { subType: 'quick_reply', payload: [emptyVariable('static')] }] });
  };

  const handleRemoveButton = (index: number) => {
    onChange({ ...builder, buttons: buttons.filter((_, i) => i !== index) });
  };

  const handleAddButtonPayloadVariable = (buttonIndex: number) => {
    onChange({
      ...builder,
      buttons: buttons.map((b, i) => (i === buttonIndex ? { ...b, payload: [...b.payload, emptyVariable('static')] } : b)),
    });
  };

  const handleButtonPayloadVariableChange = (buttonIndex: number, varIndex: number, variable: ApprovalVariableSourceUI) => {
    onChange({
      ...builder,
      buttons: buttons.map((b, i) =>
        i === buttonIndex ? { ...b, payload: b.payload.map((v, j) => (j === varIndex ? variable : v)) } : b,
      ),
    });
  };

  const handleRemoveButtonPayloadVariable = (buttonIndex: number, varIndex: number) => {
    onChange({
      ...builder,
      buttons: buttons.map((b, i) => (i === buttonIndex ? { ...b, payload: b.payload.filter((_, j) => j !== varIndex) } : b)),
    });
  };

  const handleButtonUniqueActionToggle = (buttonIndex: number, uniqueAction: boolean) => {
    onChange({
      ...builder,
      buttons: buttons.map((b, i) => (i === buttonIndex ? { ...b, uniqueAction } : b)),
    });
  };

  return (
    <VStack align="stretch" spacing={3} pl={3} borderLeftWidth="2px" borderColor="border-default">
      <FormControl>
        <Checkbox
          isChecked={!!builder.header}
          onChange={(e) => handleHeaderToggle(e.target.checked)}
          colorScheme="brand"
          borderColor="border-muted"
          sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
        >
          Template has a header
        </Checkbox>
      </FormControl>

      {builder.header && (
        <Box pl={3} borderLeftWidth="2px" borderColor="border-default">
          <FormControl mb={2}>
            <FormLabel fontSize="xs" color="text-secondary">Header type</FormLabel>
            <Select
              size="sm"
              bg="bg-surface"
              value={builder.header.type}
              onChange={(e) => {
                const nextType = e.target.value as ApprovalTemplateHeaderUI['type'];
                const isText = nextType === 'text';
                const sourceMatchesType = isText === TEXT_KINDS.includes(builder.header!.source.kind);
                handleHeaderChange({
                  ...builder.header!,
                  type: nextType,
                  // A media-only kind (image_link, etc.) can't fill a text header and vice
                  // versa — reset to a sane default for the new type rather than carrying
                  // over a now-invalid source.
                  source: sourceMatchesType ? builder.header!.source : emptyVariable(isText ? 'current_value' : 'image_link'),
                });
              }}
            >
              <option value="image">Image</option>
              <option value="document">Document</option>
              <option value="video">Video</option>
              <option value="text">Text</option>
            </Select>
          </FormControl>

          <FormControl mb={2}>
            <FormLabel fontSize="xs" color="text-secondary">Header source</FormLabel>
            <VariableRow
              variable={builder.header.source}
              kindOptions={builder.header.type === 'text' ? BODY_KIND_OPTIONS : MEDIA_HEADER_SOURCE_KIND_OPTIONS}
              availableTasks={availableTasks}
              onChange={(source) => handleHeaderChange({ ...builder.header!, source })}
            />
          </FormControl>

          {builder.header.source.kind === 'generated_image' && (
            <FormControl>
              <FormLabel fontSize="xs" color="text-secondary">Value to render as the image</FormLabel>
              <VariableRow
                variable={builder.header.valueSource || emptyVariable('current_value')}
                kindOptions={BODY_KIND_OPTIONS}
                availableTasks={availableTasks}
                onChange={(valueSource) => handleHeaderChange({ ...builder.header!, valueSource })}
              />
            </FormControl>
          )}
        </Box>
      )}

      <FormControl>
        <FormLabel fontSize="sm" color="text-primary">
          Body variables
        </FormLabel>
        <Text fontSize="xs" color="text-muted" mb={2}>
          One per {'{{n}}'} placeholder in the WhatsApp template, in order.
        </Text>
        <VStack align="stretch" spacing={2}>
          {body.map((variable, index) => (
            <VariableRow
              key={index}
              variable={variable}
              kindOptions={BODY_KIND_OPTIONS}
              availableTasks={availableTasks}
              onChange={(updated) => handleBodyVariableChange(index, updated)}
              onRemove={() => handleRemoveBodyVariable(index)}
            />
          ))}
        </VStack>
        <Button
          leftIcon={<MdAdd />}
          size="sm"
          variant="outline"
          mt={2}
          onClick={handleAddBodyVariable}
        >
          Add body variable
        </Button>
      </FormControl>

      {allowButtons && (
        <FormControl>
          <FormLabel fontSize="sm" color="text-primary">
            Buttons
          </FormLabel>
          <Text fontSize="xs" color="text-muted" mb={2}>
            One per quick-reply button the WhatsApp template declares, in order. The payload is
            one or more variables concatenated with no separator — e.g. a static "/start CODE "
            piece followed by a value from a task — and is what a backend action matches against
            when the button is tapped. On a maker-checker level, defining any button here
            replaces the built-in approve/reject buttons entirely — use the "Approval decision"
            variable kind to build those buttons yourself.
          </Text>
          <VStack align="stretch" spacing={2}>
            {buttons.map((button, buttonIndex) => (
              <Box key={buttonIndex} borderWidth="1px" borderColor="border-default" borderRadius="md" p={2} bg="bg-surface">
                <HStack spacing={2} align="center" mb={2}>
                  <Text fontSize="xs" color="text-secondary">Quick reply</Text>
                  <IconButton
                    icon={<MdDelete />}
                    aria-label="Remove button"
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    ml="auto"
                    onClick={() => handleRemoveButton(buttonIndex)}
                  />
                </HStack>
                <VStack align="stretch" spacing={2}>
                  {button.payload.map((variable, varIndex) => (
                    <VariableRow
                      key={varIndex}
                      variable={variable}
                      kindOptions={BUTTON_PAYLOAD_KIND_OPTIONS}
                      availableTasks={availableTasks}
                      onChange={(updated) => handleButtonPayloadVariableChange(buttonIndex, varIndex, updated)}
                      onRemove={() => handleRemoveButtonPayloadVariable(buttonIndex, varIndex)}
                    />
                  ))}
                </VStack>
                <Button
                  leftIcon={<MdAdd />}
                  size="sm"
                  variant="outline"
                  mt={2}
                  onClick={() => handleAddButtonPayloadVariable(buttonIndex)}
                >
                  Add payload variable
                </Button>
                <Checkbox
                  mt={2}
                  isChecked={!!button.uniqueAction}
                  onChange={(e) => handleButtonUniqueActionToggle(buttonIndex, e.target.checked)}
                  colorScheme="brand"
                  borderColor="border-muted"
                  sx={{ '[data-checked]': { borderColor: 'brand.500', backgroundColor: 'brand.500' } }}
                >
                  <Text fontSize="xs">
                    Unique action — only the first tap runs this button; later taps are told it was already handled
                  </Text>
                </Checkbox>
              </Box>
            ))}
          </VStack>
          <Button
            leftIcon={<MdAdd />}
            size="sm"
            variant="outline"
            mt={2}
            onClick={handleAddButton}
          >
            Add button
          </Button>
        </FormControl>
      )}
    </VStack>
  );
};
